import { flexRender } from '@tanstack/react-table';
import type { HeaderGroup, Header } from '@tanstack/react-table';

import { TableHeaderProps, TableRowData } from './types';

import Checkbox from '~/core/atoms/Checkbox';
import ArrowIcon from '~/core/iconComponents/icons/ArrowIcon';
import cn from '~/utils/cn';

const ARROW_SIZE = 14;

const TableHeader = <T extends TableRowData>({ table, selection, className }: TableHeaderProps<T>) => {
  const handleSelectAllChange = (checked: boolean) => {
    if (checked) {
      table.toggleAllRowsSelected(true);
    } else {
      table.toggleAllRowsSelected(false);
    }
  };

  const isAllSelected = table.getIsAllRowsSelected();
  const isSomeSelected = table.getIsSomeRowsSelected();

  return (
    <thead className={cn('bg-bluesky-100', className)}>
      {table.getHeaderGroups().map((headerGroup: HeaderGroup<T>) => (
        <tr key={headerGroup.id}>
          {/* Selection column */}
          {selection?.enabled && (
            <th className="w-40 p-8 text-left first:pl-12">
              {selection.showSelectAll !== false && (
                <Checkbox
                  size="sm"
                  checked={isAllSelected}
                  indeterminate={isSomeSelected}
                  onChange={handleSelectAllChange}
                  ariaLabel="Select all rows"
                />
              )}
            </th>
          )}

          {/* Data columns */}
          {headerGroup.headers.map((header: Header<T, unknown>) => {
            // Access custom properties from the original column definition
            const customColumnDef = header.column.columnDef;
            const width = customColumnDef.size || 'auto';
            const sortDirection = header.column.getIsSorted();
            const canSort = header.column.getCanSort();

            return (
              <th
                key={header.id}
                className={cn(
                  'text-grey-700 px-12 py-16 text-left text-sm font-medium tracking-wider uppercase first:pl-12',
                  {
                    'cursor-pointer hover:bg-black/3': canSort,
                  }
                )}
                style={{ width }}
                onClick={header.column.getToggleSortingHandler()}
              >
                <div className="flex items-center gap-4">
                  <span>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </span>
                  {/* Sort indicators */}

                  {canSort && sortDirection && (
                    <div className="flex flex-col items-center justify-center">
                      <ArrowIcon
                        direction={sortDirection === 'asc' ? 'up' : 'down'}
                        size={ARROW_SIZE}
                        className="transition-transform duration-300"
                      />
                    </div>
                  )}
                </div>
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );
};

export default TableHeader;
